import { z } from 'zod';
import { type AnyType } from './any';
import { type AudiencePreviewPayload } from './audiences';
import { CampaignScheduleType } from './CampaignScheduleType';
import { type CampaignStatus } from './CampaignStatus';
import { CommunicationChannel, type Primitive } from './communications';
import { type Period, PeriodType } from './deliveryControl';
import { AUDIENCE_IN_EVENT, AUDIENCE_OUT_EVENT } from './events';
import {
    CampaignFieldType,
    type CampaignType,
    FieldType,
    type MaterializationFieldType,
    type Schedule,
} from './field';
import { type CampaignThrottleConfig } from './integrations';
import { type CronConfig, type JourneyFilterGroup } from './journeys';
import {
    CLICKED_EVENT_STATUS,
    CONVERSION_EVENT_STATUS,
    DELIVERED_EVENT_STATUS,
    EventStatus,
    OPENED_EVENT_STATUS,
} from './srtAnalytics';
import {
    type Pagination,
    PushDeliveryPriority,
    type TemplateContentDetails,
    WAHeaderType,
} from './templates';
import { type LightdashUser } from './user';

export enum AudienceType {
    WAREHOUSE = 'WAREHOUSE',
    CSV = 'CSV',
    SINGLE_SEND = 'SINGLE_SEND',
}

export enum TemplateType {
    EXTERNAL = 'EXTERNAL',
    INTERNAL = 'INTERNAL',
}

export const FIRST_SYNC_TIME = 1000 * 60 * 10; // 10 minutes

export type Variable = {
    value: string;
    defaultValue: string;
    allowNull?: boolean;
    type: MaterializationFieldType;
    enableTracking?: boolean;
    calculatedValue?: string; // Added for dynamic alias involving wh fields
};
export type ContentMapping = Record<string, Variable>;

export enum ContentMappingSection {
    HEADER = 'header',
    SUBJECT = 'subject',
    BODY = 'body',
    BUTTONS = 'buttons',
    LINK_TRACKING = 'link_tracking',
    JOURNEYS_METADATA = 'journeys_metadata',
    PREHEADER = 'preheader',
}

export enum PushContentMappingSection {
    MESSAGE = 'message',
    TITLE = 'title',
}

export type WAMappings = {
    header?: { [key: string]: Variable };
    body?: { [key: string]: Variable };
    buttons?: { [key: string]: Variable };
};
export type EmailMappings = {
    subject?: { [key: string]: Variable };
    body?: { [key: string]: Variable };
    link_tracking?: { [key: string]: Variable };
    preheader?: { [key: string]: Variable };
};
export type SMSMappings = {
    body?: { [key: string]: Variable };
};
export type JourneysMappings = {
    journeys_metadata?: { [key: string]: Variable };
};
export type PushMappings = {
    message?: { [key: string]: Variable };
    title?: { [key: string]: Variable };
};

export type ContentMappings = WAMappings &
    EmailMappings &
    SMSMappings &
    JourneysMappings &
    PushMappings;

export type CampaignConversionConfig = {
    events: CampaignConversionEvent[];
};

export type CampaignConversionEvent = {
    id: string;
    eventName: string;
    eventSource: string;
    deadline?: number;
    filterConfig?: JourneyFilterGroup;
    uiConfig?: Partial<{
        timeGranularity: PeriodType;
    }>;
    associatedAudienceIds?: string[];
    cronConfig?: CronConfig;
};

export function hasAssociatedAudiences(
    event: CampaignConversionEvent,
): boolean {
    return (
        event.associatedAudienceIds != null &&
        event.associatedAudienceIds.length > 0
    );
}

export const CAMPAIGN_NAME_LIMIT = 50;

export type CommunicationDetails = {
    id: string;
    providerId: string;
    eventId: string | undefined;
    runDetails?: CampaignRunDetails[];
    eventName?: string;
    templateId?: string;
};

export type CampaignVariantCommunicationDetails = Record<
    string,
    CommunicationDetails
>;

export type CampaignRunDetails = {
    runAt: number;
    uploadIds: string[];
    audienceCount: number;
    materializedCampaignCount: number;
    ignoredEntityCount: number;
    campaignAudienceIdInternal?: string;
    exclusionsSnapshotName: string;
    deliveryControlRuleLimits: DeliveryControlRuleLimit[];
};

export type DeliveryControlRuleLimit = {
    ruleId: string;
    limit: number;
    channel?: CommunicationChannel;
    period?: Period;
};

export type TemplateDetails = {
    id: string;
    version?: number | null;
    type: TemplateType;
    integrationId?: string;
};

export type AudienceDetailsWarehouse = {
    audienceId: string;
    audienceType: AudienceType.WAREHOUSE;
};

export type CSVUploadDetailsType = {
    uploadId: string;
    fileName: string;
    audienceCount: number;
    columns: Array<string>;
    blobPath: string;
};

export type AudienceDetailsCSV = {
    csvUploadDetails: CSVUploadDetailsType;
    audienceType: AudienceType.CSV;
};
export type TrackingParameters = {
    [key: string]: string;
};
export type SendTo = {
    [key: string]: string;
    type: FieldType;
};

export type SenderDetails = {
    name?: string;
    fromEmail?: string;
    replyToEmail?: string;
    senderProfileId?: string;
};

export enum CampaignResponseIncludes {
    CHILDREN_COUNT = 'childrenCount',
    STATUS_COUNT = 'statusCount',
}

export enum CampaignQueryParams {
    NAME = 'name',
    FILTER = 'filter',
    CHANNEL = 'channel',
    INCLUDES_TAGS = 'includesTags',
    EXCLUDES_TAGS = 'excludesTags',
    SORT = 'sort',
    ORDER = 'order',
    TAB = 'tab',
    RAN_ATLEAST_ONCE = 'ranAtLeastOnce',
    EXECUTION_TIME_NOT_NULL = 'executionTimeNotNull',
    REDUCED = 'reduced',
    EXECUTED_AFTER_DATETIME = 'executionTimeAfterDatetime',
    EXECUTED_BEFORE_DATETIME = 'executionTimeBeforeDatetime',
    LAST_EXECUTED_AFTER_DATETIME = 'lastExecutedAfterDatetime',
    TYPE = 'type',
    VIEW_ID = 'viewId',
    FILTERS = 'filters',
    EXPERIMENT_NOT_NULL = 'experimentNotNull',
    INCLUDES = 'includes',
}

export enum CommonQueryParams {
    PER_PAGE = 'perPage',
    CURRENT_PAGE = 'currentPage',
    CREATED_AFTER = 'createdAfter',
    CREATED_BEFORE = 'createdBefore',
    GRANULARITY = 'granularity',
}

export const MAX_CAMPAIGN_RETRIES = 3;
export const CAMPAIGN_RETRY_DELAY = 5000;

export type CampaignFilters = {
    name?: string;
    id?: string;
    idIn?: string[];
    status?: string[];
    conversionConfigExists?: boolean;
    type?: string;
    audienceType?: AudienceType;
    includesTags?: string;
    excludesTags?: string;
    parentCampaignId?: string;
    isParent?: boolean;
    channel?: CommunicationChannel;
    createdAfter?: Date;
    createdBefore?: Date;
    executionTimeBeforeDatetime?: Date;
    executionTimeAfterDatetime?: Date;
    ranAtLeastOnce?: boolean;
    executionTimeNotNull?: boolean;
    lastExecutedAfterDatetime?: Date;
    viewId?: string;
    filters?: string;
    experimentNotNull?: boolean;
};

export type CampaignOverrides = {
    frequencyCapping?: boolean;
    quietHours?: boolean;
    enableChannelIdDedupe?: boolean;
};

export type CampaignExperimentVariant = {
    id: string;
    name?: string;
    distributionPercentage: number;
    isControl: boolean;
    winningVariant: boolean;
};

export enum CampaignExperimentStrategy {
    ALL = 'all',
    SUBSET = 'subset',
}

export enum CampaignWinningVariantStrategy {
    AUTO = 'auto',
    MANUAL = 'manual',
}

export type CampaignExperiment = {
    variants: CampaignExperimentVariant[];
    winningMetric: CampaignConversionConfig;
    hypothesis: string;
    testStrategy: {
        type: CampaignExperimentStrategy;
        distributionValue?: number;
    };
    scaleWinningVariantStrategy: CampaignWinningVariantStrategy;
    controlGroup: {
        enabled: boolean;
        distributionValue?: number;
    };
};

export type CampaignTemplateDetails = Record<string, TemplateDetails>;

export type CampaignContentDetails = Record<string, TemplateContentDetails>;

export type CampaignContentMappings = Record<string, ContentMappings>;

export type CampaignTrackingParameters = Record<string, TrackingParameters>;

export type Campaign = {
    id: string;
    organizationId: string;
    projectId: string;
    name: string;
    description?: string;
    channel: CommunicationChannel;
    status?: CampaignStatus;
    isDeleted?: boolean;
    communicationDetails?: CampaignVariantCommunicationDetails;
    audienceId?: string | null;
    audienceType?: AudienceType;
    csvUploadDetails?: CSVUploadDetailsType | null;
    templateDetails?: CampaignTemplateDetails;
    sendTo?: SendTo;
    subscriptionGroupId?: string;
    contentMappings?: CampaignContentMappings;
    contentDetails?: CampaignContentDetails;
    schedule?: Schedule;
    campaignTrackingParameters?: CampaignTrackingParameters;
    createdBy?: string;
    updatedBy?: string;
    createdAt?: Date | undefined;
    updatedAt?: Date | undefined;
    tags?: string[] | undefined;
    type?: CampaignType;
    parentId?: string;
    senderDetails?: SenderDetails;
    throttleConfig?: CampaignThrottleConfig | null;
    conversionConfig?: CampaignConversionConfig | null;
    previewText?: {
        [key: string]: string;
    };
    audienceConfig?: CampaignAudienceConfig;
    overrides?: CampaignOverrides;
    experiment?: CampaignExperiment | null;
};

export type ReducedCampaignType = Omit<
    Campaign,
    | 'audienceId'
    | 'templateDetails'
    | 'sendTo'
    | 'subscriptionGroupId'
    | 'contentMappings'
    | 'contentDetails'
    | 'campaignTrackingParameters'
    | 'senderDetails'
    | 'throttleConfig'
    | 'conversionConfig'
    | 'previewText'
    | 'createdBy'
    | 'updatedBy'
>;

export enum ReservedCampaignTags {
    INTERNAL = 'srt_internal',
    TRANSIENT = 'srt_transient',
}

export type CampaignAudienceConfig = {
    totalCount?: number;
    config: {
        in: string[];
        notIn: string[];
    };
    lastEstimatedAt?: Date;
};

export type CampaignAudiencePreviewPayload = {
    channel: CommunicationChannel;
    campaignAudienceConfig: CampaignAudienceConfig;
    previewConfig: Pick<
        AudiencePreviewPayload,
        'limit' | 'offset' | 'dimensions' | 'metrics' | 'sorts'
    >;
};

export type WarehouseAudienceMetadata = {
    audienceId: string | null;
    audienceName: string | null;
    audienceType: AudienceType.WAREHOUSE;
};

export type CSVAudienceMetadata = {
    uploadId: string;
    fileName: string;
    audienceType: AudienceType.CSV;
};

export type ExtendedCampaign = Partial<
    Omit<Campaign, 'createdBy' | 'updatedBy'>
> & {
    audienceCount?: number;
    audienceName?: string;
    createdBy: Pick<LightdashUser, 'firstName' | 'lastName' | 'userUuid'>;
    updatedBy: Pick<LightdashUser, 'firstName' | 'lastName' | 'userUuid'>;
    uploadId?: string;
};

export type ReducedCampaign = Partial<
    Omit<ReducedCampaignType, 'createdBy' | 'updatedBy'>
> & {
    audienceCount?: number;
    audienceName?: string;
    createdBy: Pick<LightdashUser, 'firstName' | 'lastName' | 'userUuid'>;
    updatedBy: Pick<LightdashUser, 'firstName' | 'lastName' | 'userUuid'>;
    uploadId?: string;
};

export type CampaignList = {
    data: ExtendedCampaign[] | null;
    paginate?: Pagination;
    stats?: Array<Record<string, number>>;
};

export type ReducedCampaignList = {
    data: ReducedCampaign[] | null;
    paginate?: Pagination;
    stats?: Array<Record<string, number>>;
};

export type ApiCampaign = {
    status: 'ok';
    results: Campaign;
};

export type ApiCampaignList = {
    status: 'ok';
    results: CampaignList;
};

export type ApiReducedCampaignList = {
    status: 'ok';
    results: ReducedCampaignList;
};

export type ApiCampaignRuns = {
    status: 'ok';
    results: Pick<CampaignList, 'data' | 'paginate'>;
};

export type CampaignCreateRequest = Pick<Campaign, 'name' | 'channel'> &
    Partial<Pick<Campaign, 'description' | 'tags'>>;

export type EstimateAudienceCountPayload = {
    channel: CommunicationChannel;
    campaignAudienceConfig: CampaignAudienceConfig;
};

export type CampaignUpdateRequest = Partial<Campaign>;

export type PublishCampaignRequest = {
    campaignId: string;
};

export type UnpublishCampaignRequest = PublishCampaignRequest;

export type TriggerCampaignRequest = {
    scheduleAt: number;
};

export type CampaignTagsUpdateRequest = {
    tags: string[];
};

export type CampaignStatusResponse = {
    id: string | undefined;
    status: CampaignStatus | undefined;
};

export type CampaignWithAudienceDetails = Campaign &
    Partial<{
        audienceName: string;
        audienceCount: number;
        failureReason: string;
    }>;

export type ExternalCampaignTriggerRequest = Partial<
    Pick<
        Campaign,
        | 'organizationId'
        | 'projectId'
        | 'channel'
        | 'communicationDetails'
        | 'templateDetails'
        | 'sendTo'
        | 'subscriptionGroupId'
        | 'contentDetails'
        | 'contentMappings'
        | 'schedule'
        | 'campaignTrackingParameters'
        | 'previewText'
        | 'senderDetails'
    >
>;
const PeriodSchema = z.object({
    type: z.nativeEnum(PeriodType, {
        invalid_type_error: 'Period type must be a valid period type',
    }),
    value: z.number({
        invalid_type_error: 'Period value must be a number',
    }),
});
const DeliveryControlRuleLimitSchema = z.object({
    ruleId: z.string({
        invalid_type_error: 'Rule ID must be a string',
    }),
    limit: z.number({
        invalid_type_error: 'Limit must be a number',
    }),
    channel: z
        .nativeEnum(CommunicationChannel, {
            invalid_type_error: 'Channel must be a valid communication channel',
        })
        .optional(),
    period: PeriodSchema.optional(),
});
const CampaignRunDetailsSchema = z.object({
    runAt: z.number({
        invalid_type_error: 'Run at must be a number',
    }),
    uploadIds: z.array(z.string(), {
        invalid_type_error: 'Upload IDs must be an array of strings',
    }),
    audienceCount: z.number({
        invalid_type_error: 'Audience count must be a number',
    }),
    materializedCampaignCount: z.number({
        invalid_type_error: 'Materialized campaign count must be a number',
    }),
    ignoredEntityCount: z.number({
        invalid_type_error: 'Ignored entity count must be a number',
    }),
    campaignAudienceIdInternal: z
        .string({
            invalid_type_error:
                'Campaign audience ID internal must be a string',
        })
        .optional(),
    exclusionsSnapshotName: z.string({
        invalid_type_error: 'Exclusions snapshot name must be a string',
    }),
    deliveryControlRuleLimits: z.array(DeliveryControlRuleLimitSchema, {
        invalid_type_error: 'Delivery control rule limits must be an array',
    }),
});
const SendToSchema = z
    .object({
        type: z.nativeEnum(FieldType, {
            invalid_type_error: 'Send to type must be a valid field type',
        }),
    })
    .catchall(
        z.string({
            invalid_type_error: 'Send to additional properties must be strings',
        }),
    );

// Template content schemas for ExternalCampaignTriggerRequest
const WAHeaderSchema = z.object({
    type: z.nativeEnum(WAHeaderType, {
        invalid_type_error: 'WA header type must be a valid header type',
    }),
    text: z
        .string({
            invalid_type_error: 'WA header text must be a string',
        })
        .optional(),
    url: z
        .string({
            invalid_type_error: 'WA header URL must be a string',
        })
        .optional(),
    sample: z
        .array(z.string(), {
            invalid_type_error: 'WA header sample must be an array of strings',
        })
        .optional(),
});

const WABodySchema = z.object({
    text: z.string({
        invalid_type_error: 'WA body text must be a string',
    }),
    sample: z
        .array(z.string(), {
            invalid_type_error: 'WA body sample must be an array of strings',
        })
        .optional(),
});

const WAFooterSchema = z.object({
    text: z.string({
        invalid_type_error: 'WA footer text must be a string',
    }),
});

const WAButtonSchema = z.object({
    type: z
        .string({
            invalid_type_error: 'WA button type must be a string',
        })
        .optional(),
    text: z
        .string({
            invalid_type_error: 'WA button text must be a string',
        })
        .optional(),
    title: z
        .string({
            invalid_type_error: 'WA button title must be a string',
        })
        .optional(),
    id: z
        .string({
            invalid_type_error: 'WA button ID must be a string',
        })
        .optional(),
    isDynamic: z
        .string({
            invalid_type_error: 'WA button isDynamic must be a string',
        })
        .optional(),
    is_dynamic: z
        .string({
            invalid_type_error: 'WA button is_dynamic must be a string',
        })
        .optional(),
    domain: z
        .string({
            invalid_type_error: 'WA button domain must be a string',
        })
        .optional(),
    url: z
        .string({
            invalid_type_error: 'WA button URL must be a string',
        })
        .optional(),
    index: z
        .number({
            invalid_type_error: 'WA button index must be a number',
        })
        .optional(),
    phone: z
        .string({
            invalid_type_error: 'WA button phone must be a string',
        })
        .optional(),
    value: z
        .string({
            invalid_type_error: 'WA button value must be a string',
        })
        .optional(),
    sample: z
        .string({
            invalid_type_error: 'WA button sample must be a string',
        })
        .optional(),
});

const WaVariableDetailsSchema = z.object({
    [ContentMappingSection.HEADER]: z
        .array(z.string(), {
            invalid_type_error:
                'WA header variables must be an array of strings',
        })
        .optional(),
    [ContentMappingSection.BODY]: z
        .array(z.string(), {
            invalid_type_error: 'WA body variables must be an array of strings',
        })
        .optional(),
    [ContentMappingSection.BUTTONS]: z
        .array(z.string(), {
            invalid_type_error:
                'WA button variables must be an array of strings',
        })
        .optional(),
});

const SMSVariableDetailsSchema = z.object({
    [ContentMappingSection.BODY]: z
        .array(z.string(), {
            invalid_type_error:
                'SMS body variables must be an array of strings',
        })
        .optional(),
});

const EmailVariableDetailsSchema = z.object({
    [ContentMappingSection.SUBJECT]: z
        .array(z.string(), {
            invalid_type_error:
                'Email subject variables must be an array of strings',
        })
        .optional(),
    [ContentMappingSection.BODY]: z
        .array(z.string(), {
            invalid_type_error:
                'Email body variables must be an array of strings',
        })
        .optional(),
    [ContentMappingSection.PREHEADER]: z
        .array(z.string(), {
            invalid_type_error:
                'Email preheader variables must be an array of strings',
        })
        .optional(),
});

const TemplateVariableDetailsSchema = WaVariableDetailsSchema.merge(
    SMSVariableDetailsSchema,
).merge(EmailVariableDetailsSchema);

const ParsedVariableSchema = z.object({
    key: z.string({
        invalid_type_error: 'Parsed variable key must be a string',
    }),
    type: z.string({
        invalid_type_error: 'Parsed variable type must be a string',
    }),
    value: z.string({
        invalid_type_error: 'Parsed variable value must be a string',
    }),
});

// MaterializationFieldType schema - union of FieldType and CampaignFieldType
const MaterializationFieldTypeSchema = z.union(
    [
        z.nativeEnum(FieldType, {
            invalid_type_error:
                'Materialization field type must be a valid field type',
        }),
        z.nativeEnum(CampaignFieldType, {
            invalid_type_error:
                'Materialization field type must be a valid campaign field type',
        }),
    ],
    {
        invalid_type_error:
            'Materialization field type must be a valid field type or campaign field type',
    },
);

const VariableSchema = z.object({
    value: z.string({
        invalid_type_error: 'Variable value must be a string',
    }),
    defaultValue: z.string({
        invalid_type_error: 'Variable default value must be a string',
    }),
    allowNull: z
        .boolean({
            invalid_type_error: 'Variable allow null must be a boolean',
        })
        .optional(),
    type: MaterializationFieldTypeSchema,
    enableTracking: z
        .boolean({
            invalid_type_error: 'Variable enable tracking must be a boolean',
        })
        .optional(),
    calculatedValue: z
        .string({
            invalid_type_error: 'Variable calculated value must be a string',
        })
        .optional(),
});

const WAMappingsSchema = z.object({
    header: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'WA header mappings must be a record of variables',
        })
        .optional(),
    body: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'WA body mappings must be a record of variables',
        })
        .optional(),
    buttons: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'WA button mappings must be a record of variables',
        })
        .optional(),
});

const EmailMappingsSchema = z.object({
    subject: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'Email subject mappings must be a record of variables',
        })
        .optional(),
    body: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'Email body mappings must be a record of variables',
        })
        .optional(),
    link_tracking: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'Email link tracking mappings must be a record of variables',
        })
        .optional(),
    preheader: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'Email preheader mappings must be a record of variables',
        })
        .optional(),
});

const SMSMappingsSchema = z.object({
    body: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'SMS body mappings must be a record of variables',
        })
        .optional(),
});

const JourneysMappingsSchema = z.object({
    journeys_metadata: z
        .record(z.string(), VariableSchema, {
            invalid_type_error:
                'Journeys metadata mappings must be a record of variables',
        })
        .optional(),
});

const ContentMappingsSchema = WAMappingsSchema.merge(EmailMappingsSchema)
    .merge(SMSMappingsSchema)
    .merge(JourneysMappingsSchema);

const EmailTemplateContentDetailsSchema = z.object({
    subject: z
        .string({
            invalid_type_error: 'Email template subject must be a string',
        })
        .optional(),
    html: z.string({
        invalid_type_error: 'Email template HTML must be a string',
    }),
    json: z
        .record(z.unknown(), {
            invalid_type_error: 'Email template JSON must be a record',
        })
        .optional(),
    variables: TemplateVariableDetailsSchema.optional(),
    parsedVariables: z
        .array(ParsedVariableSchema, {
            invalid_type_error:
                'Email template parsed variables must be an array',
        })
        .optional(),
});

const SmsTemplateContentDetailsSchema = z.object({
    body: z.string({
        invalid_type_error: 'SMS template body must be a string',
    }),
    dltTemplateId: z
        .string({
            invalid_type_error: 'SMS DLT template ID must be a string',
        })
        .optional(),
    unicode: z
        .boolean({
            invalid_type_error: 'SMS unicode must be a boolean',
        })
        .optional(),
    flash: z
        .boolean({
            invalid_type_error: 'SMS flash must be a boolean',
        })
        .optional(),
    variables: TemplateVariableDetailsSchema.optional(),
});

const WhatsAppTemplateContentDetailsSchema = z.object({
    header: WAHeaderSchema,
    body: WABodySchema,
    footer: WAFooterSchema,
    buttons: z.array(WAButtonSchema, {
        invalid_type_error: 'WhatsApp template buttons must be an array',
    }),
    language: z
        .string({
            invalid_type_error: 'WhatsApp template language must be a string',
        })
        .optional(),
});

const PushTemplateContentDetailsSchema = z.object({
    title: z.string({
        invalid_type_error: 'Push template title must be a string',
    }),
    message: z.string({
        invalid_type_error: 'Push template message must be a string',
    }),
    actionUrl: z
        .string({
            invalid_type_error: 'Push template action URL must be a string',
        })
        .optional(),
    deliveryPriority: z
        .nativeEnum(PushDeliveryPriority, {
            invalid_type_error:
                'Push delivery priority must be a valid priority value',
        })
        .optional(),
    collapseKey: z
        .string({
            invalid_type_error: 'Push template collapse key must be a string',
        })
        .optional(),
    additionalData: z
        .record(z.unknown(), {
            invalid_type_error:
                'Push template additional data must be a record',
        })
        .optional(),
    variables: TemplateVariableDetailsSchema.optional(),
});

const TemplateContentDetailsSchema = z.union(
    [
        EmailTemplateContentDetailsSchema,
        SmsTemplateContentDetailsSchema,
        WhatsAppTemplateContentDetailsSchema,
        PushTemplateContentDetailsSchema,
    ],
    {
        invalid_type_error:
            'Template content details must be a valid template type',
    },
);

const RecurringDetailsSchema = z.object({
    endTime: z
        .date({
            invalid_type_error:
                'Recurring details end time must be a valid date',
        })
        .optional()
        .nullable(),
    cron: z.string({
        invalid_type_error: 'Recurring details cron must be a string',
    }),
});

const ScheduleSchema = z.object({
    type: z.nativeEnum(CampaignScheduleType, {
        invalid_type_error:
            'Schedule type must be a valid campaign schedule type',
    }),
    executionTime: z
        .date({
            invalid_type_error: 'Schedule execution time must be a valid date',
        })
        .optional()
        .nullable(),
    jobId: z
        .string({
            invalid_type_error: 'Schedule job ID must be a string',
        })
        .optional(),
    recurringDetails: RecurringDetailsSchema.optional(),
    children: z
        .number({
            invalid_type_error: 'Schedule children must be a number',
        })
        .optional(),
    firedAt: z
        .date({
            invalid_type_error: 'Schedule fired at must be a valid date',
        })
        .optional()
        .nullable(),
    nextExecution: z
        .date({
            invalid_type_error: 'Schedule next execution must be a valid date',
        })
        .optional()
        .nullable(),
    lastExecuted: z
        .date({
            invalid_type_error: 'Schedule last executed must be a valid date',
        })
        .optional()
        .nullable(),
    isExecutionInProgress: z
        .boolean({
            invalid_type_error:
                'Schedule is execution in progress must be a boolean',
        })
        .optional()
        .nullable(),
});

const TrackingParametersSchema = z.record(z.string(), {
    invalid_type_error: 'Tracking parameters must be a record of strings',
});

const SenderDetailsSchema = z.object({
    name: z
        .string({
            invalid_type_error: 'Sender name must be a string',
        })
        .optional(),
    fromEmail: z
        .string({
            invalid_type_error: 'Sender from email must be a string',
        })
        .optional(),
    replyToEmail: z
        .string({
            invalid_type_error: 'Sender reply to email must be a string',
        })
        .optional(),
    senderProfileId: z
        .string({
            invalid_type_error: 'Sender profile ID must be a string',
        })
        .optional(),
});

// Schema for CommunicationDetails
const CommunicationDetailsSchema = z.object({
    id: z
        .string({
            invalid_type_error: 'Communication details ID must be a string',
        })
        .optional(),
    providerId: z
        .string({
            invalid_type_error: 'Provider ID must be a string',
        })
        .optional(),
    eventId: z
        .string({
            invalid_type_error: 'Event ID must be a string',
        })
        .optional(),
    runDetails: z
        .array(CampaignRunDetailsSchema, {
            invalid_type_error: 'Communication run details must be an array',
        })
        .optional(),
    eventName: z
        .string({
            invalid_type_error: 'Communication event name must be a string',
        })
        .optional(),
    templateId: z
        .string({
            invalid_type_error: 'Communication template ID must be a string',
        })
        .optional(),
});

// Schema for TemplateDetails
const TemplateDetailsSchema = z.object({
    id: z
        .string({
            invalid_type_error: 'Template ID must be a string',
        })
        .optional(),
    version: z
        .union([z.number(), z.null()], {
            invalid_type_error: 'Template version must be a number or null',
        })
        .optional(),
    type: z
        .nativeEnum(TemplateType, {
            invalid_type_error: 'Template type must be a valid template type',
        })
        .optional(),
    integrationId: z
        .string({
            invalid_type_error: 'Template integration ID must be a string',
        })
        .optional(),
});

// Zod schema for ExternalCampaignTriggerRequest (partial pick of Campaign)
export const ExternalCampaignTriggerRequestSchema = z.object(
    {
        organizationId: z
            .string({
                invalid_type_error: 'Organization ID must be a string',
            })
            .optional(),
        projectId: z
            .string({
                invalid_type_error: 'Project ID must be a string',
            })
            .optional(),
        channel: z
            .nativeEnum(CommunicationChannel, {
                invalid_type_error:
                    'Channel must be a valid communication channel',
            })
            .optional(),
        communicationDetails: z
            .record(z.string(), CommunicationDetailsSchema, {
                invalid_type_error:
                    'Communication details must be a record of variant communication details',
            })
            .optional(),
        templateDetails: z
            .record(z.string(), TemplateDetailsSchema, {
                invalid_type_error:
                    'Template details must be a record of variant template details',
            })
            .optional(),
        sendTo: SendToSchema.optional(),
        subscriptionGroupId: z
            .string({
                invalid_type_error: 'Subscription group ID must be a string',
            })
            .optional(),
        contentDetails: z
            .record(z.string(), TemplateContentDetailsSchema, {
                invalid_type_error:
                    'Content details must be a record of variant content details',
            })
            .optional(),
        contentMappings: z
            .record(z.string(), ContentMappingsSchema, {
                invalid_type_error:
                    'Content mappings must be a record of variant content mappings',
            })
            .optional(),
        schedule: ScheduleSchema.optional(),
        campaignTrackingParameters: z
            .record(z.string(), TrackingParametersSchema, {
                invalid_type_error:
                    'Campaign tracking parameters must be a record of variant tracking parameters',
            })
            .optional(),
        previewText: z
            .record(z.string(), z.string(), {
                invalid_type_error:
                    'Preview text must be a record of variant preview text',
            })
            .optional(),
        senderDetails: SenderDetailsSchema.optional(),
    },
    {
        invalid_type_error:
            'Invalid config type for external campaign trigger request',
    },
);

export type UserDetails = {
    userId: string;
    email?: string;
    phone?: string;
};

export enum CampaignAnalyticsOverviewStatuses {
    PROCESSED = 'Processed',
    OPEN = 'Open',
    DELIVERED = 'Delivered',
    REJECTED = 'Rejected',
    TOTAL_REQUESTS = 'TotalRequests',
    CONVERTED = 'CampaignConverted',
}

export type CampaignAnalyticsOverviewResponse = Partial<
    Record<CampaignAnalyticsOverviewStatuses, number>
>;

export const unsubEndpoint = '/unsubscribe';
export const unsubPreferencesEndpoint = '/subscription-preferences/channel';

export const unsubUrl = `{{site_url}}${unsubEndpoint}?subToken={{sub_token}}`;
export const unsubPreferencesUrl = `{{site_url}}${unsubPreferencesEndpoint}?subToken={{sub_token}}`;
export const emailOneClickUnsubscribeUrl = `{{site_url}}/api/v1/user-subscription-preferences/one-click-unsubscribe?subToken={{sub_token}}`;

export const BASE_FUNNEL_CHART_ITEMS = [
    DELIVERED_EVENT_STATUS,
    OPENED_EVENT_STATUS,
    CLICKED_EVENT_STATUS,
    CONVERSION_EVENT_STATUS,
];
export const BASE_FUNNEL_CHART_TITLE = '1. Base Funnel';

export const BASE_FUNNEL_CHART_VALUES_MAP = {
    [DELIVERED_EVENT_STATUS]: [
        EventStatus.DELIVERED,
        EventStatus.UNDELIVERED,
        EventStatus.BOUNCE,
    ],
    [OPENED_EVENT_STATUS]: [EventStatus.OPEN, EventStatus.READ],
    [CLICKED_EVENT_STATUS]: [EventStatus.CLICKED],
    [CONVERSION_EVENT_STATUS]: [EventStatus.CONVERTED],
};

export const validateCampaignName = (name: string): boolean => {
    const regex = /^[A-Za-z0-9_\s.-]{1,256}$/;
    return regex.test(name);
};

export const trimCampaignName = (name: string): string =>
    name.replace(/[^A-Za-z0-9_\s.-]/g, '').substring(0, CAMPAIGN_NAME_LIMIT);

export const previewTextVariable = '{{previewText}}';

export enum UnsubUrlQueryParam {
    TEST = 'test',
}
export type JourneySingleSendPayload = Omit<FynoSingleSendPayload, 'event'> & {
    nodeName?: string;
};

export type FynoSingleSendPayload = {
    event: string;
    to: NotificationTo;
    data: {
        [key: string]: Primitive;
    };
    callback?: {
        custom_id?: string;
        custom1?: string;
        custom2?: string;
    };
    distinct_id?: string;
    idempotency_key?: string;
};

export enum SendMessageMode {
    TEST = 'test',
    LIVE = 'live',
}

export type NotificationTo = {
    [CommunicationChannel.SMS]?: string;
    [CommunicationChannel.EMAIL]?: string;
    [CommunicationChannel.WHATSAPP]?: string;
    [CommunicationChannel.PUSH]?: string;
    [CommunicationChannel.SLACK]?: string;
    [CommunicationChannel.ANY]?: string;
    [CommunicationChannel.UNKNOWN]?: string;
};

export type CampaignPayloadValidationErrors = {
    communicationDetails: string | undefined;
    sendTo: string | undefined;
    subscriptionGroupId: string | undefined;
    audience: string | undefined;
    subject:
        | {
              [variantKey: string]: string;
          }
        | undefined;
    contentMappings:
        | {
              [variantKey: string]: {
                  [groupName: string]: {
                      [variable: string]: string;
                  };
              };
          }
        | undefined;
    throttleConfig?: string | undefined;
};

export enum CampaignSortKeys {
    SCHEDULED_AT = 'scheduledAt',
    LAST_EXECUTED = 'lastExecuted',
}

export enum ReservedSourceNames {
    FYNO = 'fyno',
}

export enum CampaignConversionEventType {
    PERFORMED_EVENT = 'PERFORMED_EVENT',
    AUDIENCE_IN = 'AUDIENCE_IN',
    AUDIENCE_OUT = 'AUDIENCE_OUT',
}

// Zod validation schemas with comprehensive error messages

const PeriodTypeSchema = z.enum(['DAY', 'WEEK', 'MONTH', 'HOUR', 'MINUTE'], {
    errorMap: () => ({
        message: 'Period type must be one of: DAY, WEEK, MONTH, HOUR, MINUTE',
    }),
});

const CronConfigSchema = z.object(
    {
        cron: z
            .string({
                invalid_type_error: 'Cron expression must be a string',
            })
            .optional(),
        jobId: z
            .string({
                invalid_type_error: 'Job ID must be a string',
            })
            .optional(),
        cronStartAt: z
            .date({
                invalid_type_error:
                    'Cron start date must be a valid Date object',
            })
            .optional(),
        cronEndAt: z
            .date({
                invalid_type_error: 'Cron end date must be a valid Date object',
            })
            .optional(),
    },
    {
        invalid_type_error: 'Cron config must be an object',
    },
);

// Recursive schema for JourneyFilterGroup
const JourneyFieldTargetSchema = z.object(
    {
        fieldId: z
            .string({
                required_error: 'Field ID is required',
                invalid_type_error: 'Field ID must be a string',
            })
            .min(1, 'Field ID cannot be empty'),
        fieldRef: z
            .string({
                invalid_type_error: 'Field reference must be a string',
            })
            .optional(),
    },
    {
        required_error: 'Journey field target is required',
        invalid_type_error: 'Journey field target must be an object',
    },
);

const JourneyConditionalOperatorSchema = z.enum(
    [
        'isNull',
        'notNull',
        'equals',
        'notEquals',
        'startsWith',
        'endsWith',
        'include',
        'doesNotInclude',
        'lessThan',
        'lessThanOrEqual',
        'greaterThan',
        'greaterThanOrEqual',
        'inBetween',
        'equalsCaseInsensitive',
        'notEqualsCaseInsensitive',
        'inThePast',
        'notInThePast',
        'inTheNext',
        'notInTheNext',
        'inTheCurrent',
        'notInTheCurrent',
    ],
    {
        errorMap: () => ({
            message:
                'Invalid conditional operator. Must be one of: isNull, notNull, equals, notEquals, startsWith, endsWith, include, doesNotInclude, lessThan, lessThanOrEqual, greaterThan, greaterThanOrEqual, inBetween, equalsCaseInsensitive, notEqualsCaseInsensitive, inThePast, notInThePast, inTheNext, notInTheNext, inTheCurrent, notInTheCurrent',
        }),
    },
);

const JourneyFilterRuleSchema = z.object(
    {
        target: JourneyFieldTargetSchema,
        operator: JourneyConditionalOperatorSchema,
        values: z
            .array(z.union([z.string(), z.number(), z.boolean(), z.null()]), {
                invalid_type_error: 'Filter values must be an array',
            })
            .optional(),
        settings: z
            .object({
                unitOfTime: z
                    .enum(
                        [
                            'milliseconds',
                            'seconds',
                            'minutes',
                            'hours',
                            'days',
                            'weeks',
                            'months',
                            'years',
                        ],
                        {
                            invalid_type_error:
                                'Unit of time must be one of: milliseconds, seconds, minutes, hours, days, weeks, months, years',
                        },
                    )
                    .optional(),
                completed: z
                    .boolean({
                        invalid_type_error: 'Completed must be a boolean',
                    })
                    .optional(),
            })
            .optional(),
    },
    {
        required_error: 'Journey filter rule is required',
        invalid_type_error: 'Journey filter rule must be an object',
    },
);

// Explicit type annotation for the recursive schema
const JourneyFilterGroupSchema: z.ZodType<AnyType> = z.lazy(() =>
    z.union(
        [
            z.object(
                {
                    or: z
                        .array(
                            z.union([
                                JourneyFilterGroupSchema,
                                JourneyFilterRuleSchema,
                            ]),
                            {
                                required_error:
                                    'OR filter group requires an array of filters',
                                invalid_type_error:
                                    'OR filters must be an array',
                            },
                        )
                        .min(
                            1,
                            'OR filter group must contain at least one filter',
                        ),
                },
                {
                    invalid_type_error: 'OR filter group must be an object',
                },
            ),
            z.object(
                {
                    and: z
                        .array(
                            z.union([
                                JourneyFilterGroupSchema,
                                JourneyFilterRuleSchema,
                            ]),
                            {
                                required_error:
                                    'AND filter group requires an array of filters',
                                invalid_type_error:
                                    'AND filters must be an array',
                            },
                        )
                        .min(
                            1,
                            'AND filter group must contain at least one filter',
                        ),
                },
                {
                    invalid_type_error: 'AND filter group must be an object',
                },
            ),
        ],
        {
            errorMap: () => ({
                message: "Filter group must have either 'or' or 'and' property",
            }),
        },
    ),
);

export const JourneyFiltersConfigSchema = z.object(
    {
        id: z
            .string({
                required_error: 'Filter config ID is required',
                invalid_type_error: 'Filter config ID must be a string',
            })
            .min(1, 'Filter config ID cannot be empty'),
        journeyFilters: JourneyFilterGroupSchema.optional(),
        audienceFilters: z
            .object(
                {
                    filterJoinType: z.enum(['and', 'or'], {
                        errorMap: () => ({
                            message: "Filter join type must be 'and' or 'or'",
                        }),
                    }),
                    filterConfig: z.any({
                        invalid_type_error: 'Filter config is required',
                    }), // NestedMetricQueryGroup is complex, using z.any() for now
                    compiledAudienceId: z
                        .string({
                            invalid_type_error:
                                'Compiled audience ID must be a string',
                        })
                        .optional(),
                },
                {
                    invalid_type_error: 'Audience filters must be an object',
                },
            )
            .optional(),
    },
    {
        required_error: 'Journey filters config is required',
        invalid_type_error: 'Journey filters config must be an object',
    },
);

// Update the CampaignConversionEventSchema
export const CampaignConversionEventSchema = z
    .object(
        {
            id: z
                .string({
                    required_error: 'Conversion event ID is required',
                    invalid_type_error: 'Conversion event ID must be a string',
                })
                .min(1, 'Conversion event ID cannot be empty'),
            eventName: z
                .string({
                    required_error: 'Event name is required',
                    invalid_type_error: 'Event name must be a string',
                })
                .min(1, 'Select an event to track user activity'),
            eventSource: z
                .string({
                    required_error: 'Event source is required',
                    invalid_type_error: 'Event source must be a string',
                })
                .min(1, 'Event source cannot be empty'),
            deadline: z
                .number({
                    invalid_type_error:
                        'Deadline must be a number (milliseconds)',
                })
                .positive('Enter a valid tracking window (0+)')
                .optional(),
            filterConfig: JourneyFiltersConfigSchema.optional(),
            uiConfig: z
                .object(
                    {
                        timeGranularity: PeriodTypeSchema,
                    },
                    {
                        invalid_type_error: 'UI config must be an object',
                    },
                )
                .partial()
                .optional(),
            associatedAudienceIds: z
                .array(
                    z
                        .string({
                            invalid_type_error: 'Audience ID must be a string',
                        })
                        .min(1, 'Audience ID cannot be empty'),
                    {
                        invalid_type_error:
                            'Associated audience IDs must be an array of strings',
                    },
                )
                .min(1, 'Associated audience IDs array cannot be empty')
                .optional(),
            cronConfig: CronConfigSchema.optional(),
        },
        {
            required_error: 'Campaign conversion event is required',
            invalid_type_error: 'Campaign conversion event must be an object',
        },
    )
    .refine(
        (data) => {
            // Make associatedAudienceIds mandatory for AUDIENCE_IN and AUDIENCE_OUT events
            const requiresAudienceIds =
                data.eventName === AUDIENCE_IN_EVENT ||
                data.eventName === AUDIENCE_OUT_EVENT;

            if (requiresAudienceIds) {
                return (
                    data.associatedAudienceIds &&
                    data.associatedAudienceIds.length > 0
                );
            }

            return true; // For other event types, associatedAudienceIds is optional
        },
        {
            message: 'Select an audience for tracking user entry/exit',
            path: ['associatedAudienceIds'],
        },
    );

export const CampaignConversionConfigSchema = z.object(
    {
        events: z
            .array(CampaignConversionEventSchema, {
                required_error: 'Conversion events array is required',
                invalid_type_error: 'Events must be an array',
            })
            .min(1, 'At least one conversion event is required'),
    },
    {
        required_error: 'Campaign conversion config is required',
        invalid_type_error: 'Campaign conversion config must be an object',
    },
);

// Type inference from Zod schema
export type CampaignConversionConfigZod = z.infer<
    typeof CampaignConversionConfigSchema
>;
